import { useLoaderData } from "@remix-run/react";
import { LoaderFunction } from "@remix-run/server-runtime";
import { useEffect } from "react";
import invariant from "tiny-invariant";
import { ClientSide } from "~/@types";
import { IExpert, SpecificLocale } from "~/@types/generated/contentful";
import ExpertBio from "~/components/experts/ExpertBioPage";
import { FourOFourCaughtData } from "~/components/shared/FourOFour";
import { generateBreadcrumbFromRequest } from "~/components/utils/breadcrumbSchema.server";
import { getTwicPicsURL } from "~/components/utils/getTwicPicsURL";
import { pageView, track } from "~/components/utils/gtm.client";
import SchemaOrg from "~/components/utils/SchemaOrg";
import {
  getExpertSchema,
  SchemaOrgType,
} from "~/components/utils/schemaOrgUtils.server";
import { getEntryBySlug } from "~/contentful/index.server";
import * as routeUtils from "~/contentful/route-utils";
import { ComponentDataProvider } from "~/contexts/ComponentDataProvider";
import { i18n } from "~/i18n.server";

export const meta = routeUtils.meta;

export const handle = routeUtils.handle("expert");

interface LoaderData {
  expert: ClientSide<IExpert>;
  meta: routeUtils.MetaInfo;
  schemaOrg: SchemaOrgType;
  breadcrumbSchema: SchemaOrgType | null;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  const locale = await i18n.getLocale(request);

  const invariantMessage =
    "expected slug to be passed as an argument or params.slug to be defined";
  const slugToQuery = params.expertSlug;

  invariant(slugToQuery, invariantMessage);

  const expert = (await getEntryBySlug("expert", slugToQuery, {
    locale,
  })) as SpecificLocale<IExpert>;

  if (!expert) {
    throw new Response(
      // This is me cheating as a way to pass the color through
      JSON.stringify({
        message: "Expert Not Found",
        backgroundColor: "white",
        notFound: "expert",
      } as FourOFourCaughtData),
      { status: 404 },
    );
  }

  const fullName = `${expert.fields.firstName} ${expert.fields.lastName}`;

  const openGraphImageUrl = expert.fields.image?.fields
    ? getTwicPicsURL(expert.fields.image.fields, { width: 1200, format: "jpg" })
    : "";

  const breadcrumbSchema = generateBreadcrumbFromRequest({
    request,
    expertName: fullName,
  });

  return {
    expert,
    meta: routeUtils.getMetaInfo({
      title: fullName,
      description: expert.fields.role ?? fullName,
      requestUrl: request.url,
      openGraphImageUrl,
    }),
    schemaOrg: getExpertSchema(expert),
    breadcrumbSchema,
  } as LoaderData;
};

export default function ExpertPage() {
  const { expert, schemaOrg, breadcrumbSchema } = useLoaderData<LoaderData>();

  useEffect(() => {
    track(
      pageView({
        category: "Expert",
        name: expert.fields.firstName + " " + expert.fields.lastName,
      }),
    );
  }, [expert]);

  const combinedSchemas = breadcrumbSchema
    ? [schemaOrg, breadcrumbSchema]
    : [schemaOrg];

  return (
    <ComponentDataProvider componentData={{}}>
      <SchemaOrg content={combinedSchemas} />
      <ExpertBio expert={expert} />
    </ComponentDataProvider>
  );
}

export const ErrorBoundary = routeUtils.ContentfulErrorBoundary;

export const shouldRevalidate = routeUtils.shouldRevalidate;
